import { useEffect, useRef } from 'react';
import { PaginationState } from '../TanstackTable/types';
import { AutoPageSizeTableProps } from './useAutoPageSize';

/**
 * `useAutoPageSize`'s props plus the controlled pagination the pagination hooks
 * own. Pagination rides on its own prop rather than inside `state`, so spreading
 * this onto a table cannot clobber a page's own controlled sorting or filters.
 */
export type PaginatedTableProps = AutoPageSizeTableProps & {
  pagination: PaginationState;
  onPaginationChange: (pagination: PaginationState) => void;
};

/**
 * Options shared by the pagination hooks.
 */
export interface PaginationResetOptions {
  /**
   * Values that redefine the result set from *outside* the table — a date range,
   * an account picker, anything the page feeds to its query. Page index returns
   * to the first page whenever one of them changes, because "page 4" of a new
   * result set means nothing to the reader.
   *
   * The table's own sorting, search and column filters are handled internally
   * and must not be listed here.
   */
  resetOn?: unknown[];
}

/** Value equality — pagination handlers are called with a fresh object each time. */
function samePagination(a: PaginationState, b: PaginationState): boolean {
  return a.pageIndex === b.pageIndex && a.pageSize === b.pageSize;
}

/**
 * Keeps the previous object when the value has not changed.
 *
 * Pagination handlers hand out a newly built `PaginationState` on every call,
 * including calls that change nothing — TanStack's `autoResetPageIndex` fires
 * one whenever the `data` array's *identity* changes, which for a page that
 * writes `data={rows ?? []}` is every single render. Storing that new object
 * would re-render the owner, rebuild `data`, and trigger the reset again: a
 * loop that only closes once pagination state lives above the table, as it does
 * here. Returning the previous object breaks it.
 */
export function nextPagination(previous: PaginationState, next: PaginationState): PaginationState {
  return samePagination(previous, next) ? previous : next;
}

/**
 * Runs `reset` whenever any value in `dependencies` changes, but never on the
 * first render — mounting is not a change, and resetting there would fight a
 * page that restores a saved page index.
 */
export function useResetOnChange(dependencies: unknown[] | undefined, reset: () => void): void {
  const signature = JSON.stringify(dependencies ?? null);
  const lastSignature = useRef(signature);

  useEffect(() => {
    if (lastSignature.current === signature) return;
    lastSignature.current = signature;
    reset();
  }, [signature, reset]);
}
